import { Component } from '@theme/component';

export const MAX_ARTWORK_BYTES = 10 * 1024 * 1024;

/**
 * The original file is submitted unchanged. These checks are a customer-facing limit, not server validation.
 * @param {File | undefined | null} file
 * @returns {'required' | 'type' | 'size' | null}
 */
export function getArtworkFileError(file) {
  if (!file) return 'required';
  if (!/\.(jpe?g|png)$/i.test(file.name) || (file.type && !['image/jpeg', 'image/png'].includes(file.type))) return 'type';
  if (!Number.isFinite(file.size) || file.size <= 0) return 'type';
  if (file.size > MAX_ARTWORK_BYTES) return 'size';
  return null;
}

/**
 * @typedef {Object} ArtworkRefs
 * @property {HTMLInputElement} fileInput
 * @property {HTMLElement} preview
 * @property {HTMLImageElement} previewImage
 * @property {HTMLElement} filename
 * @property {HTMLElement} actions
 * @property {HTMLButtonElement} replaceButton
 * @property {HTMLButtonElement} removeButton
 * @property {HTMLElement} status
 * @property {HTMLElement} error
 */

/**
 * Validates and previews a local image. The owning product form sends the File using FormData.
 * Emits bhg:artwork-status with {valid, pending, uploading, hasFile} when readiness changes.
 * @extends {Component<ArtworkRefs>}
 */
class BHGArtworkUpload extends Component {
  requiredRefs = ['fileInput', 'preview', 'previewImage', 'filename', 'actions', 'replaceButton', 'removeButton', 'status', 'error'];
  #controller;
  #file;
  #objectUrl;
  #decoder;
  #selection = 0;
  #valid = false;
  #pending = false;
  #uploading = false;
  #validationError = '';
  #lastState = '';

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.refs.fileInput.addEventListener('change', this.#selectFile, { signal });
    this.refs.fileInput.addEventListener('invalid', this.#onInvalid, { signal });
    this.#selectFile();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.refs.fileInput.files?.[0] !== this.#file) this.#selectFile();
    else this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#selection++;
    this.#releasePreview();
  }

  /** @returns {boolean} Whether this exact selected file is ready for the native product form. */
  validate() {
    if (this.refs.fileInput.files?.[0] !== this.#file) this.#selectFile();
    if (this.#uploading) return false;
    const error = this.#pending ? this.dataset.errorPending
      : this.#validationError || (!this.#file ? this.dataset.errorRequired : '');
    if (error || !this.#valid) {
      this.#setError(error || this.dataset.errorInvalid || '');
      this.refs.fileInput.focus();
      return false;
    }
    this.#clearError();
    return true;
  }

  /** @param {boolean} uploading */
  setUploading(uploading) {
    this.#uploading = Boolean(uploading);
    this.setAttribute('aria-busy', String(this.#uploading));
    // Never disable this input: disabled controls are omitted from FormData.
    this.refs.fileInput.inert = this.#uploading;
    this.refs.fileInput.setAttribute('aria-disabled', String(this.#uploading));
    this.#render();
  }

  /** Display a retryable form/request error without invalidating the selected image. @param {string} message */
  showError(message) {
    this.#setError(message);
    this.refs.error.focus();
  }

  /** Choose another original file without altering the currently selected file if the dialog is cancelled. */
  replaceImage() {
    if (!this.#uploading) this.refs.fileInput.click();
  }

  /** Remove the selected file and its local preview. */
  removeImage() {
    if (this.#uploading) return;
    this.refs.fileInput.value = '';
    this.#selectFile();
    this.refs.fileInput.focus();
  }

  #onInvalid = (event) => {
    event.preventDefault();
    this.validate();
  };

  #selectFile = async () => {
    const selection = ++this.#selection;
    this.#releasePreview();
    this.#file = this.refs.fileInput.files?.[0];
    this.#valid = false;
    this.#pending = false;
    this.#validationError = '';
    this.refs.fileInput.setCustomValidity('');
    this.refs.fileInput.removeAttribute('aria-invalid');
    this.#clearError();
    if (!this.#file) {
      this.#render();
      return;
    }
    const fileError = getArtworkFileError(this.#file);
    if (fileError) {
      this.#reject(this.dataset[`error${fileError[0].toUpperCase()}${fileError.slice(1)}`] || '');
      return;
    }
    this.#pending = true;
    this.refs.fileInput.setCustomValidity(this.dataset.errorPending || '');
    this.#render();
    try {
      const bytes = new Uint8Array(await this.#file.slice(0, 8).arrayBuffer());
      if (selection !== this.#selection) return;
      const jpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
      const png = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((value, index) => bytes[index] === value);
      if (!jpeg && !png) throw new Error('Invalid image signature');
      this.#objectUrl = URL.createObjectURL(this.#file);
      const decoder = new Image();
      this.#decoder = decoder;
      decoder.src = this.#objectUrl;
      await decoder.decode();
      if (selection !== this.#selection) return;
      if (!decoder.naturalWidth || !decoder.naturalHeight) throw new Error('Invalid image dimensions');
      this.#decoder = undefined;
      this.#pending = false;
      this.#valid = true;
      this.refs.fileInput.setCustomValidity('');
      this.refs.fileInput.removeAttribute('aria-invalid');
      this.#clearError();
      this.refs.previewImage.src = this.#objectUrl;
      this.#render();
    } catch {
      if (selection !== this.#selection) return;
      this.#releasePreview();
      this.#reject(this.dataset.errorInvalid || '');
    }
  };

  #reject(message) {
    this.#pending = false;
    this.#valid = false;
    this.#validationError = message;
    this.refs.fileInput.setCustomValidity(message);
    this.refs.fileInput.setAttribute('aria-invalid', 'true');
    this.#setError(message);
    this.#render();
  }

  #releasePreview() {
    this.#decoder?.removeAttribute('src');
    this.#decoder = undefined;
    this.refs.previewImage?.removeAttribute('src');
    if (this.#objectUrl) URL.revokeObjectURL(this.#objectUrl);
    this.#objectUrl = undefined;
  }

  #setError(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = !message;
  }

  #clearError() {
    this.#setError('');
  }

  #render() {
    this.refs.preview.hidden = !this.#valid;
    this.refs.filename.textContent = this.#file?.name || '';
    this.refs.actions.hidden = !this.#file;
    this.refs.replaceButton.disabled = this.#uploading;
    this.refs.removeButton.disabled = this.#uploading;
    this.refs.status.textContent = this.#uploading ? this.dataset.uploading || ''
      : this.#pending ? this.dataset.checking || ''
        : this.#valid ? this.dataset.ready || '' : '';
    const detail = { valid: this.#valid, pending: this.#pending, uploading: this.#uploading, hasFile: !!this.#file };
    const state = JSON.stringify(detail);
    if (state !== this.#lastState) {
      this.#lastState = state;
      this.dispatchEvent(new CustomEvent('bhg:artwork-status', { bubbles: true, detail }));
    }
  }
}

if (!customElements.get('bhg-artwork-upload')) {
  customElements.define('bhg-artwork-upload', BHGArtworkUpload);
}
